import cn from "classnames";
import { isValidElement, Children } from "react";
import flattenChildren from "react-keyed-flatten-children";

import { getBorderRadiusProps } from "@/components/BorderRadius/BorderRadius";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./Row.module.scss";
import { Cell } from "../Cell/Cell";

interface RowProps extends Required<React.PropsWithChildren> {
  /**
   * When true, row gap will be applied to the row.
   */
  hasRowGap?: boolean;

  /**
   * When true, row border radius will be applied to the row.
   */
  hasRowBorderRadius?: boolean;

  /**
   * When true, row border radius will be not be applied between stacked rows.
   */
  noYAxisRowGap?: boolean;

  /**
   * When true, cells will be in reversed order.
   */
  isReversed?: boolean;

  /**
   * Class name(s) to customize component if required.
   */
  className?: string;

  /**
   * Ref to Row component.
   */
  divRef?: React.Ref<HTMLDivElement>;
}

interface CellContainerProps extends Required<React.PropsWithChildren> {
  className: string;
}

const CellContainer = ({ className, children }: CellContainerProps) => (
  <div className={cn(styles.double, styles.cellContainer, className)}>{children}</div>
);

interface WrapperProps extends Required<React.PropsWithChildren> {
  isReversed?: boolean;
}

const Wrap3ChildRow = ({ children, isReversed }: WrapperProps) => {
  const [child1, ...rest] = Children.toArray(children);

  return (
    <>
      {child1}
      <CellContainer
        className={cn(styles.forceTwoCellRowToWrap, {
          [styles.isReversed]: isReversed,
        })}
      >
        {rest}
      </CellContainer>
    </>
  );
};

const Wrap4ChildRow = ({ children, isReversed }: WrapperProps) => {
  const [child1, child2, ...rest] = Children.toArray(children);

  return (
    <>
      <CellContainer
        className={cn({
          [styles.isReversed]: isReversed,
        })}
      >
        {child1}
        {child2}
      </CellContainer>
      <CellContainer
        className={cn({
          [styles.isReversed]: isReversed,
        })}
      >
        {rest}
      </CellContainer>
    </>
  );
};

/**
 * `Row` component is intended to be used as grid layout and it should be used inside `SectionContentContainer`. It Will only
 * allow `Cell` and Fragment component as children, others will be ignored.
 * `Row` component allows you to put up to 4 `Cell` components in a row, and cells will be wrap based on cell min-width, row gap and row width.
 * You can add row gap and row border radius to the row, and also cell border radius to the cell and you can reverse
 * the order of cells.
 *
 * If `Row` has gaps, consumers cannot set row border radius but can set border radius for `Cell` component.
 * If `Row` does not have row gap, consumers cannot set cell border radius.
 *
 * Usage:
 *
 * ```tsx
 * <Section container="outer">
 *   <SectionContentContainer width="12col">
 *     <Row>
 *       <Cell>Cell 2</Cell>
 *       <Cell>Cell 2</Cell>
 *     </Row>
 *   </SectionContentContainer>
 * </Section>
 * ```
 */
export const Row = ({
  children,
  hasRowGap,
  noYAxisRowGap,
  hasRowBorderRadius,
  isReversed,
  className,
  divRef,
}: RowProps) => {
  // Flatten the children and filter out Fragments, keeping only Cell components
  const processedChildren = flattenChildren(children).filter(
    (child) => isValidElement(child) && child.type === Cell
  );

  const extraCellsCount = processedChildren.reduce((count: number, child) => {
    if (
      // To appease typescript
      isValidElement<{ className?: string }>(child) &&
      typeof child.props?.className === "string" &&
      // Actual logic
      !child.props.className.includes("cellContainer")
    ) {
      if (child.props.className.includes("double")) count += 1;
      if (child.props.className.includes("triple")) count += 2;
    }
    return count;
  }, 0);

  const setRowStyles = {
    "--lk-private-cells-in-row": processedChildren.length + extraCellsCount,
  } as React.CSSProperties;

  let component = <>{processedChildren}</>;
  if (processedChildren.length === 3) {
    component = <Wrap3ChildRow isReversed={isReversed}>{processedChildren}</Wrap3ChildRow>;
  }

  if (processedChildren.length === 4) {
    component = <Wrap4ChildRow isReversed={isReversed}>{processedChildren}</Wrap4ChildRow>;
  }

  return (
    <div
      ref={divRef}
      {...mergeProps(
        { style: setRowStyles },
        hasRowBorderRadius && !hasRowGap ? getBorderRadiusProps("extra-large") : null,
        {
          className: cn(
            styles.row,
            {
              [styles.noGap]: !hasRowGap,
              [styles.noRowGap]: noYAxisRowGap,
              [styles.isReversed]: isReversed,
            },
            className
          ),
        }
      )}
    >
      {component}
    </div>
  );
};
